import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Snackbar, Box, Typography, IconButton } from '@mui/material';
import { CloseCircle, Warning2, TickCircle, InfoCircle } from 'iconsax-react';
import { useConfig } from './ConfigProvider';

export const TOAST_TYPES = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success',
} as const;

export type ToastType = typeof TOAST_TYPES[keyof typeof TOAST_TYPES];

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const TOAST_CONFIG: Record<ToastType, { color: string; borderColor: string; bg: string }> = {
  error:   { color: '#E20F0F', borderColor: '#E6E8EC', bg: '#FFFFFF' },
  warning: { color: '#E67E22', borderColor: '#E6E8EC', bg: '#FFFFFF' },
  success: { color: '#1E9E5E', borderColor: '#E6E8EC', bg: '#FFFFFF' },
  info:    { color: '#1A6FB5', borderColor: '#E6E8EC', bg: '#FFFFFF' },
};

const TOAST_ICONS: Record<ToastType, React.FC<{ color: string }>> = {
  error:   ({ color }) => <CloseCircle size={20} color={color} variant="Bold" />,
  warning: ({ color }) => <Warning2 size={20} color={color} variant="Bold" />,
  success: ({ color }) => <TickCircle size={20} color={color} variant="Bold" />,
  info:    ({ color }) => <InfoCircle size={20} color={color} variant="Bold" />,
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { direction } = useConfig();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<ToastType>('error');
  const [toastKey, setToastKey] = useState(0);

  const showToast = useCallback((msg: string, toastType: ToastType = 'error') => {
    setOpen(false);
    setMessage(msg);
    setType(toastType);
    setToastKey(k => k + 1);
    requestAnimationFrame(() => setOpen(true));
  }, []);

  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const { color, borderColor, bg } = TOAST_CONFIG[type];
  const Icon = TOAST_ICONS[type];

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Snackbar
        key={`${toastKey}-${direction}`}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ top: { xs: 16, sm: 24 }, zIndex: theme => theme.zIndex.snackbar + 100 }}
      >
        <Box
          dir={direction}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            width: 'fit-content',
            maxWidth: 'min(480px, calc(100vw - 32px))',
            px: 2,
            py: 1.25,
            borderRadius: '999px',
            border: `1.5px solid ${borderColor}`,
            backgroundColor: bg,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ flexShrink: 0, display: 'flex' }}>
            <Icon color={color} />
          </Box>
          <Typography
            sx={{
              fontSize: '0.9rem',
              fontWeight: 500,
              color,
              lineHeight: 1.5,
            }}
          >
            {message}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ p: 0.25, color, flexShrink: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.7 } }}
          >
            <CloseCircle size={16} color={color} />
          </IconButton>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  );
};
